import { useQuery } from '@tanstack/react-query';
import { ApiError } from '../http';
import {
  getRiskMonitor,
  getPositionRisk,
  PositionRiskMonitor,
  RiskMonitor,
} from '../api/riskMonitorApi';

/**
 * Shape both risk hooks return. `error` is normalised to `undefined` (react-query
 * types it `Error | null`), and the 404 "not computed yet" case is surfaced via
 * `notReady` instead of as an error.
 */
export interface RiskQueryState<T> {
  data: T | undefined;
  isLoading: boolean;
  notReady: boolean;
  error: Error | undefined;
}

export function positionRiskQueryOptions(positionId: number) {
  return {
    queryKey: ['risk-monitor', 'position', positionId] as const,
    queryFn: () => getPositionRisk(positionId),
    enabled: !!positionId,
    staleTime: 5 * 60 * 1000,
    retry: (count: number, err: unknown) =>
      !(err instanceof ApiError && err.response?.status === 404) && count < 2,
  };
}

export function portfolioRiskQueryOptions() {
  return {
    queryKey: ['risk-monitor', 'portfolio'] as const,
    queryFn: () => getRiskMonitor(),
    staleTime: 5 * 60 * 1000,
    retry: (count: number, err: unknown) =>
      !(err instanceof ApiError && err.response?.status === 404) && count < 2,
  };
}

export function usePortfolioRiskMonitor(): RiskQueryState<RiskMonitor> {
  const query = useQuery<RiskMonitor, Error>(portfolioRiskQueryOptions());
  const notReady = query.error instanceof ApiError && query.error.response?.status === 404;
  return {
    data: query.data,
    isLoading: query.isLoading,
    notReady,
    error: notReady ? undefined : (query.error ?? undefined),
  };
}

export function useRiskMonitor(positionId: number): RiskQueryState<PositionRiskMonitor> {
  const query = useQuery<PositionRiskMonitor, Error>(positionRiskQueryOptions(positionId));
  const notReady = query.error instanceof ApiError && query.error.response?.status === 404;
  return {
    data: query.data,
    isLoading: query.isLoading,
    notReady,
    error: notReady ? undefined : (query.error ?? undefined),
  };
}
